'use client';

import { useClientSide } from '@carvana/merch-react-hooks';
import { MfeLoad } from '@carvana/microfrontend';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { getMfeCacheBuster } from '@/common/MicroFrontEnds';
import { commonEnvironment } from '~/constants/environment';
import { wait } from '~/utilities';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import MinifiedFooter from './MinifiedFooter/MinifiedFooter';

function getFooterClassname(pathname: string | null): string | undefined {
  if (!pathname) return undefined;

  if (pathname.startsWith('/cars')) {
    return 'bg-background-default';
  }

  if (pathname.startsWith('/discover')) {
    return 'bg-canvas-muted';
  }

  if (pathname.startsWith('/new')) {
    return 'bg-background-weak';
  }

  return undefined;
}

function getShouldUseMinifiedFooter(pathname: string | null): boolean {
  if (!pathname) return false;

  return pathname.startsWith('/new');
}

/**
 * Client Component for PageFooter
 * This is a simplfied page footer that have access to context providers
 * The server component CarvanaLayoutFooter handles the context logic
 */
function PageFooter() {
  const isClientSide = useClientSide();
  const pathname = usePathname();
  const className = getFooterClassname(pathname);
  const shouldUseMinifiedFooter = getShouldUseMinifiedFooter(pathname);

  const hasLoadedMfe = useRef(false);

  useEffect(() => {
    if (shouldUseMinifiedFooter || hasLoadedMfe.current) return;

    let cancelled = false;

    wait(100).then(() => {
      if (cancelled) return;

      hasLoadedMfe.current = true;
      void MfeLoad({
        files: [
          [
            'FooterModule',
            [
              {
                component: 'default',
                browser: true,
                default: {
                  file: 'index.js',
                  manifest: `${
                    process.env.CVNA_APP_CDN_URL
                  }/footer/module/assets-manifest.json?v=${getMfeCacheBuster()}`
                }
              }
            ]
          ]
        ]
      });
    });

    return () => {
      cancelled = true;
    };
  }, [shouldUseMinifiedFooter]);

  if (shouldUseMinifiedFooter) {
    return <MinifiedFooter className={className} />;
  }

  return isClientSide ? (
    <MfeReactComponent
      component="default"
      module="FooterModule"
      environment={commonEnvironment}
      withoutProvider
      wrapperClassName={className}
    />
  ) : null;
}

export default withErrorBoundary(PageFooter);
